import { muiInputLabelProps } from '@components/cars-table-next/filter/constants';
import { Select } from '@components/ui';
import { InputAdornment, makeStyles } from '@material-ui/core';
import { KeyboardDateTimePicker } from '@material-ui/pickers';
import { DateTime } from 'luxon';
import { Controller, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { DATE_TIME_FORMAT } from 'src/modules/date-helpers';
import { DateFilter } from 'src/modules/generated/api';
import { tr } from 'src/modules/i18n-helpers';

import { useFilter } from '../filter/useFilter';

const useStyles = makeStyles(() => ({
  datePicker: {
    width: '100%',
  },
}));

type DateFilterRadiosProps = {
  dateType: string;
  control: any;
  customOnChange: (value: string | string[] | DateTime | null) => void;
};

export const dateFilters = [
  { value: DateFilter.Last30Minutes, label: tr('carsTable.lastxOminutes', { count: 30 }) },
  { value: DateFilter.Last1Hour, label: tr('carsTable.lastxOhour', { count: 1 }) },
  { value: DateFilter.Last3Hour, label: tr('carsTable.lastxOhour', { count: 3 }) },
  { value: DateFilter.Last6Hour, label: tr('carsTable.lastxOhour', { count: 6 }) },
  { value: DateFilter.Individual, label: tr('configuration.individual') },
];

export const endDateFilters = [
  { value: DateFilter.Next30Minutes, label: tr('carsTable.nextxOminutes', { count: 30 }) },
  { value: DateFilter.Next1Hour, label: tr('carsTable.nextxOhour', { count: 1 }) },
  { value: DateFilter.Next3Hour, label: tr('carsTable.nextxOhour', { count: 3 }) },
  { value: DateFilter.Next6Hour, label: tr('carsTable.nextxOhour', { count: 6 }) },
  { value: DateFilter.Individual, label: tr('configuration.individual') },
];

export const DateTimeFilterSelect = ({ dateType, control, customOnChange }: DateFilterRadiosProps) => {
  const classes = useStyles();
  const value = useWatch({ name: dateType === 'end' ? 'endDateFilter' : 'startDateFilter', control });
  const { t } = useTranslation();
  const { setValue } = useFilter();

  return (
    <>
      {dateType === 'end' ? (
        <Controller
          name="endDateFilter"
          control={control}
          render={({ field: { ref, ...field } }) => (
            <Select
              inputRef={ref}
              {...field}
              label="carsTable.endingTime"
              clearable
              options={endDateFilters}
              InputLabelProps={muiInputLabelProps}
              {...(customOnChange && { onChange: customOnChange })}
            />
          )}
        />
      ) : (
        <Controller
          name="startDateFilter"
          control={control}
          render={({ field: { ref, ...field } }) => (
            <Select
              inputRef={ref}
              {...field}
              label="carsTable.startingTime"
              clearable
              options={dateFilters}
              InputLabelProps={muiInputLabelProps}
              {...(customOnChange && { onChange: customOnChange })}
            />
          )}
        />
      )}

      {value === DateFilter.Individual && (
        <>
          <Controller
            name={dateType === 'end' ? 'endDateFrom' : 'startDateFrom'}
            control={control}
            defaultValue={DateTime.now().startOf('day').toISO()}
            render={({ field }) => (
              <KeyboardDateTimePicker
                {...field}
                ampm={false}
                className={classes.datePicker}
                format={DATE_TIME_FORMAT}
                invalidDateMessage={t('carsTable.formError.dateTimeError')}
                InputProps={{
                  startAdornment: <InputAdornment position="start">{t('common.from')}</InputAdornment>,
                  fullWidth: true,
                }}
                inputRef={field.ref}
                inputVariant="outlined"
                margin="dense"
                openTo="date"
                placeholder="TT.MM.JJ SS:MM"
                onChange={(fieldValue) => {
                  field.onChange(fieldValue);
                  setValue(dateType === 'end' ? 'endDateFrom' : 'startDateFrom', fieldValue?.toString() ?? ''); // workaround cause only changing field state doesn't update the form state
                }}
              />
            )}
          />
          <Controller
            name={dateType === 'end' ? 'endDateTo' : 'startDateTo'}
            control={control}
            defaultValue={
              dateType === 'end'
                ? DateTime.now().endOf('day').toISO()
                : DateTime.now().plus({ hour: 1 }).startOf('hour').toISO()
            }
            render={({ field }) => (
              <KeyboardDateTimePicker
                {...field}
                ampm={false}
                className={classes.datePicker}
                format={DATE_TIME_FORMAT}
                invalidDateMessage={t('carsTable.formError.dateTimeError')}
                InputProps={{
                  startAdornment: <InputAdornment position="start">{t('common.to')}</InputAdornment>,
                  fullWidth: true,
                }}
                inputRef={field.ref}
                inputVariant="outlined"
                margin="dense"
                onChange={(fieldValue) => {
                  field.onChange(fieldValue);
                  setValue(dateType === 'end' ? 'endDateTo' : 'startDateTo', fieldValue?.toString() ?? ''); // workaround cause only changing field state doesn't update the form state
                }}
                openTo="date"
                placeholder="TT.MM.JJ SS:MM"
                name={field.name}
              />
            )}
          />
        </>
      )}
    </>
  );
};
